import React, { useCallback, useMemo } from 'react'
import { useAppSelector } from '../../../state/hooks'
import { SegmentedButton } from '../../../components/DesignSystem'

type TipFuncData = {
	tip0: () => void
	tip5: () => void
	tip10: () => void
	tip12: () => void
	tip15: () => void
	tip18: () => void
	tip20: () => void
	tipPercentage: (value: number) => void
	setCustomTip: (value: number) => void
}

type Option = {
	label: string
	value: string
	description?: string
}

type TipAmountButtonsProps = {
	prices: {
		subTotal: number
		discount: number
	}
	tipFuncData: TipFuncData
	onFocus: () => void
	isDelivery: boolean
	isCustomTipFocused: boolean
}

const DEFAULT_TIP_OPTIONS = {
	delivery: [
		{ label: 'No Tip', value: 0 },
		{ label: '5%', value: 5 },
		{ label: '10%', value: 10 },
		{ label: '15%', value: 15 },
	],
	pickup: [
		{ label: 'No Tip', value: 0 },
		{ label: '5%', value: 5 },
		{ label: '10%', value: 10 },
		{ label: '15%', value: 15 },
	],
	kiosk: [
		{ label: 'No Tip', value: 0 },
		{ label: '$1', value: 1 },
		{ label: '$2', value: 2 },
	],
}

export const TipAmountButtons = ({
	prices,
	tipFuncData,
	onFocus,
	isDelivery,
	isCustomTipFocused,
}: TipAmountButtonsProps) => {
	const { tipCount, selectedTip } = useAppSelector(state => state.checkout)
	const { customTipOptions } = useAppSelector(state => state.settings)

	const calculateTipAmount = useCallback(
		(percent: number) => {
			return percent === 0 ? '$0.00' : '$' + (prices.subTotal * (percent / 100)).toFixed(2)
		},
		[prices.subTotal]
	)

	const {
		delivery: deliveryCustomTip,
		pickup: pickupCustomTip,
		kiosk: kioskCustomTip,
	} = customTipOptions

	// Memoize tip options to avoid recalculating on every render
	const deliveryTipOptions = useMemo(() => {
		const tips = deliveryCustomTip
			? [{ label: 'No Tip', value: 0 }, ...deliveryCustomTip]
			: DEFAULT_TIP_OPTIONS.delivery

		return tips.map(tip => ({
			...tip,
			description: tip.label.endsWith('%') ? calculateTipAmount(tip.value) : null,
		}))
	}, [deliveryCustomTip, calculateTipAmount])

	const pickupTipOptions = useMemo(() => {
		const tips = pickupCustomTip
			? [{ label: 'No Tip', value: 0 }, ...pickupCustomTip]
			: DEFAULT_TIP_OPTIONS.pickup

		return tips.map(tip => ({
			...tip,
			description: tip.label.endsWith('%') ? calculateTipAmount(tip.value) : null,
		}))
	}, [pickupCustomTip, calculateTipAmount])

	const kioskTipOptions = useMemo(() => {
		const tips = kioskCustomTip
			? [{ label: 'No Tip', value: 0 }, ...kioskCustomTip]
			: DEFAULT_TIP_OPTIONS.kiosk

		return tips.map(tip => ({
			...tip,
			description: tip.label.endsWith('%') ? calculateTipAmount(tip.value) : null,
		}))
	}, [kioskCustomTip, calculateTipAmount])

	const handleTipSelect = ({ label, value }: Option) => {
		if (label.startsWith('$')) {
			return tipFuncData.setCustomTip(Number(value))
		} else {
			// For percentage tips or no tip
			return tipFuncData.tipPercentage(Number(value))
		}
	}

	const handleSelect = (option: Option) => {
		handleTipSelect(option)
		onFocus()
	}

	const options = isDelivery
		? deliveryTipOptions
		: global.kiosk
			? kioskTipOptions
			: pickupTipOptions

	const tipType = selectedTip === 9999 ? 'custom' : 'percentage'
	const selectedValue = tipType === 'custom' ? tipCount : selectedTip

	return (
		<SegmentedButton
			options={options}
			selectedValue={isCustomTipFocused ? null : selectedValue || 0}
			onSelect={handleSelect}
			size="md"
			buttonStyle={{
				paddingVertical: global.kiosk && 16,
			}}
		/>
	)
}
